import { Box, Card, Heading, HStack, SimpleGrid, Text, VStack } from '@chakra-ui/react'
import { createFileRoute } from '@tanstack/react-router'
import {
  Activity,
  Check,
  Gauge,
  Layers,
  Network,
  Server,
  ShieldCheck,
  Users,
  Zap,
} from 'lucide-react'

import { HivemqClusterSimulation } from '@/components/HivemqClusterSimulation'
import { PageHeader, SectionHeading } from '@/components/shell'

export const Route = createFileRoute('/_app/education/hivemq-cluster')({
  component: HivemqClusterPage,
})

interface PhaseCardProps {
  icon: typeof Server
  phase: string
  title: string
  description: string
}

function PhaseCard({ icon: Icon, phase, title, description }: PhaseCardProps) {
  return (
    <Card.Root transition="all 0.3s" _hover={{ shadow: 'md' }}>
      <Card.Body p={5} mt="4" mb="4">
        <VStack align="start" gap={3}>
          <HStack gap={3}>
            <Box p={2} bg="brand.solid" borderRadius="lg">
              <Icon size={20} />
            </Box>
            <Text
              fontSize="xs"
              fontWeight="bold"
              color="content.secondary"
              textTransform="uppercase"
              letterSpacing="wide"
            >
              {phase}
            </Text>
          </HStack>
          <VStack align="start" gap={1}>
            <Text fontWeight="medium">{title}</Text>
            <Text fontSize="sm" color="content.secondary">
              {description}
            </Text>
          </VStack>
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

const stats = [
  { icon: Server, value: '11', label: 'Upgrade steps' },
  { icon: Users, value: '30K', label: 'Clients connected' },
  { icon: Gauge, value: '450K/s', label: 'Throughput' },
  { icon: Activity, value: '0', label: 'Messages lost' },
]

function HivemqClusterPage() {
  const phases = [
    {
      icon: Server,
      phase: 'Baseline',
      title: 'Three-node cluster, single version',
      description:
        'Replication factor 2, ~30,000 MQTT clients balanced round-robin across node-1/2/3 on the current HiveMQ version.',
    },
    {
      icon: Layers,
      phase: 'Scale out',
      title: 'Provision a fourth node on the new version',
      description:
        'A 4th node boots on the target version, joins the cluster, syncs partitions, and is added to the load balancer pool, creating spare capacity for the rolling upgrade.',
    },
    {
      icon: Zap,
      phase: 'Rolling upgrade',
      title: 'Rotate each original node, one at a time',
      description:
        'Each node is drained, leaves the cluster gracefully, is upgraded, then rejoins. The remaining three nodes keep serving traffic, capacity headroom never drops.',
    },
    {
      icon: ShieldCheck,
      phase: 'Scale in',
      title: 'Drain and decommission the fourth node',
      description:
        'Once every original node is on the new version, the temporary 4th node is drained, leaves the cluster, and is shut down. The cluster ends on three nodes, all upgraded.',
    },
  ]

  const takeaways = [
    {
      title: 'Zero client downtime',
      description:
        'Drained clients reconnect through the load balancer to a different node. Persistent sessions resume via session takeover, subscriptions, queued messages, and retained messages survive intact.',
    },
    {
      title: 'No message loss',
      description:
        'QoS 1 and QoS 2 messages are protected by replicas on surviving nodes (replication factor 2). In-flight messages are acknowledged after replication, not after local-only write.',
    },
    {
      title: 'Mixed-version clusters are supported',
      description:
        'HiveMQ explicitly supports running a mixed-version cluster for the duration of a rolling upgrade between supported versions, that is what makes one-node-at-a-time rotation safe.',
    },
    {
      title: 'Capacity headroom is mandatory',
      description:
        'The temporary 4th node ensures throughput never depends on (n−1) nodes. Without it a 3-node cluster would temporarily run at 66% capacity during each rotation.',
    },
    {
      title: 'Readiness probes gate traffic',
      description:
        'A node is only added back to the load balancer pool once its readiness probe passes, the LB never routes clients onto a node that is still syncing or warming up.',
    },
    {
      title: 'Replication factor is verified before scaling in',
      description:
        'Before the 4th node is shut down, the cluster confirms all partitions are at full replication factor 2 on the remaining three nodes, no partition is left under-replicated.',
    },
  ]

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader
        title="HiveMQ Cluster: Rolling Upgrades"
        subtitle="How a HiveMQ broker cluster moves to a new version without dropping clients, losing messages, or ever falling below capacity."
      />

      <VStack align="stretch" gap={10}>
        {/* Tags */}
        <HStack gap={2} flexWrap="wrap" justify="center">
          {['Zero Downtime', 'High Availability', 'Replication Factor 2'].map((tag) => (
            <Box key={tag} px={3} py={1} bg="brand.solid" borderRadius="full">
              <Text fontSize="sm" fontWeight="medium" color="gray.900">
                {tag}
              </Text>
            </Box>
          ))}
        </HStack>

        {/* Stats Row */}
        <SimpleGrid columns={{ base: 2, md: 4 }} gap={4}>
          {stats.map((stat) => (
            <Card.Root key={stat.label} transition="all 0.3s" _hover={{ shadow: 'md' }}>
              <Card.Body p={5} mt="4" mb="4">
                <HStack gap={3}>
                  <Box p={2} bg="brand.solid" borderRadius="lg">
                    <stat.icon size={20} />
                  </Box>
                  <VStack align="start" gap={0}>
                    <Text fontSize="2xl" fontWeight="bold" lineHeight="1">
                      {stat.value}
                    </Text>
                    <Text fontSize="xs" color="content.secondary">
                      {stat.label}
                    </Text>
                  </VStack>
                </HStack>
              </Card.Body>
            </Card.Root>
          ))}
        </SimpleGrid>

        {/* Intro Section */}
        <VStack align="stretch" gap={4}>
          <SectionHeading title="What you are looking at" />
          <Text color="content.secondary" maxW="780px">
            The simulation below walks through the 11 phases of a zero-downtime rolling upgrade. Use{' '}
            <Text as="span" fontWeight="medium">
              Play
            </Text>{' '}
            to step through it automatically, or use{' '}
            <Text as="span" fontWeight="medium">
              Next
            </Text>{' '}
            /{' '}
            <Text as="span" fontWeight="medium">
              Back
            </Text>{' '}
            and the step dots to drive it manually. Each step reports the cluster mechanics, what
            happens to clients, the safety guarantee being enforced, and live observability metrics.
          </Text>

          {/* Simulator */}
          <Card.Root>
            <Card.Body p={6} mt="4" mb="4">
              <HivemqClusterSimulation />
            </Card.Body>
          </Card.Root>
        </VStack>

        {/* Phases Section */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            title="The four phases of a rolling upgrade"
            description="Each step in the simulator maps back to one of these ordered phases."
          />
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
            {phases.map((p) => (
              <PhaseCard key={p.phase + p.title} {...p} />
            ))}
          </SimpleGrid>
        </VStack>

        {/* Key Takeaways */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            title="Key takeaways"
            description="The guarantees that make end-to-end zero downtime possible."
          />
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
            {takeaways.map((t) => (
              <HStack key={t.title} gap={3} align="flex-start">
                <Box p={1} bg="green.100" borderRadius="full" flexShrink={0} mt={1}>
                  <Check size={12} color="var(--chakra-colors-green-600)" />
                </Box>
                <VStack align="start" gap={0}>
                  <Text fontWeight="medium">{t.title}</Text>
                  <Text fontSize="sm" color="content.secondary">
                    {t.description}
                  </Text>
                </VStack>
              </HStack>
            ))}
          </SimpleGrid>
        </VStack>

        {/* CTA */}
        <Card.Root bg="bg.subtle">
          <Card.Body p={8}>
            <HStack gap={4} align="center" flexWrap="wrap">
              <Box p={3} bg="brand.solid" borderRadius="xl">
                <Network size={24} />
              </Box>
              <VStack align="start" gap={1}>
                <Heading size="lg">Cluster operations, without compromise</Heading>
                <Text color="content.secondary">
                  Drain, upgrade, rejoin, repeat. That is the entire safety story.
                </Text>
              </VStack>
            </HStack>
          </Card.Body>
        </Card.Root>
      </VStack>
    </Box>
  )
}
